/** @jsx jsx */
import { jsx } from '@emotion/react';

import type * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { CareerRolesCollectionType } from '@coursera-web/cds-labs';
import * as Sentry from '@sentry/react';

import RelatedRolesContext from 'bundles/browse-common/contexts/RelatedRolesContext';
import { normalizeCareerRolesByDomain } from 'bundles/unified-common/hooks/useCareerRolesByDomain';
import CareerRolesBySkillDocumentNode from 'bundles/unified-common/queries/CareerRolesBySkill.graphql';
import type {
  CareerRolesBySkillQueryQuery as CareerRolesBySkillQuery,
  CareerRolesBySkillQueryQueryVariables as CareerRolesBySkillQueryVariables,
} from 'bundles/unified-common/queries/__generated__/CareerRolesBySkill';

type Props = {
  skillId: string;
  children: React.ReactNode;
};

function useCareerRolesBySkillQuery(skillId: string) {
  return useQuery<CareerRolesBySkillQuery, CareerRolesBySkillQueryVariables>(CareerRolesBySkillDocumentNode, {
    variables: {
      skillId,
    },
    context: { clientName: 'gatewayGql' },
    onError: (e) =>
      Sentry.captureException(e, {
        extra: {
          message: `Error fetching career roles by skill where skillId ="${skillId}"`,
        },
      }),
  });
}

const RelatedRolesBySkillProvider = (props: Props) => {
  const { skillId, children } = props;

  const { data, loading } = useCareerRolesBySkillQuery(skillId);

  const collection: CareerRolesCollectionType | undefined = useMemo(
    () =>
      data
        ? normalizeCareerRolesByDomain({
            id: 'none',
            entities: {
              elements: data.PathwaySkillCareerRoles?.queryRolesBySkill?.elements ?? [],
            },
          })
        : undefined,
    [data]
  );

  const value = { loading, collection };

  return <RelatedRolesContext.Provider value={value}>{children}</RelatedRolesContext.Provider>;
};

export default RelatedRolesBySkillProvider;
